import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { FiArrowUpRight } from 'react-icons/fi';

import { AppWrap, MotionWrap } from '../../wrapper';
import './Research.scss';

const interests = [
  'Computer Vision',
  'Medical Image Analysis',
  'Efficient Deep Learning',
  'Vision Transformers',
  'Image Classification',
  'Pattern Recognition',
];

const publications = [
  {
    year: '2019',
    venue: 'IC4ME2 · IEEE',
    title: 'Indigenous Fish Classification of Bangladesh Using Hybrid Features with SVM Classifier',
    authors: 'Md. Aminul Islam, Md. Rasel Howlader, Umme Habiba, Rahat Hossain Faisal, and Md. Mostafijur Rahman',
    url: 'https://ieeexplore.ieee.org/document/9036679',
  },
  {
    year: '2019',
    venue: 'ICIEV & icIVPR · IEEE',
    title: 'Automatic Medicinal Plants Classification Using Multi-channel Modified Local Gradient Pattern with SVM Classifier',
    authors: 'Umme Habiba, Md. Rasel Howlader, Md. Aminul Islam, Rahat Hossain Faisal, and Md. Mostafijur Rahman',
    url: 'https://ieeexplore.ieee.org/document/8858527',
  },
];

const Research = () => {
  const reduceMotion = useReducedMotion();

  return (
    <>
      <div className="section-heading research-heading">
        <div>
          <p className="eyebrow">03 / Research</p>
          <h2 className="head-text">Finding useful signals <span>inside complex images.</span></h2>
        </div>
        <p className="section-heading__copy">
          My research began with interpretable texture descriptors and SVM-based image
          classification. I&apos;m now interested in bringing that foundation to efficient deep
          learning, medical imaging, and vision transformers.
        </p>
      </div>

      <div className="research-interests" aria-label="Research interests">
        <p>Research interests</p>
        <ul>
          {interests.map((interest) => <li key={interest}>{interest}</li>)}
        </ul>
      </div>

      <div className="research-feature">
        <motion.article
          className="research-thesis"
          initial={reduceMotion ? false : { opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.65 }}
        >
          <div className="research-thesis__meta">
            <p>Undergraduate thesis · 2018–2019</p>
            <span>University of Barishal</span>
          </div>
          <h3>Texture Based Automatic Indigenous Fish Classification of Bangladesh</h3>
          <p className="research-thesis__summary">
            Designed an end-to-end image classification pipeline and proposed the Hybrid Local
            Binary Pattern descriptor, combining fixed and adaptive thresholding to capture sign
            and magnitude information from a single image channel.
          </p>
          <div className="research-thesis__outcomes">
            <div><strong>2,610</strong><span>Images in the public dataset</span></div>
            <div><strong>08</strong><span>Indigenous fish species</span></div>
            <div><strong>90%</strong><span>Classification accuracy</span></div>
          </div>
          <a href="https://github.com/falvee/BDIndigenousFish2019" target="_blank" rel="noreferrer">
            Explore BDIndigenousFish2019 <FiArrowUpRight />
          </a>
        </motion.article>

        <aside className="research-method">
          <p className="research-method__label">Research approach</p>
          <div>
            <span>01</span>
            <p><strong>Build the dataset</strong>Capture real-world biological image variation.</p>
          </div>
          <div>
            <span>02</span>
            <p><strong>Design the features</strong>Extract interpretable texture and gradient signals.</p>
          </div>
          <div>
            <span>03</span>
            <p><strong>Evaluate rigorously</strong>Compare kernels and descriptors under one protocol.</p>
          </div>
        </aside>
      </div>

      <div className="publications">
        <div className="publications__heading">
          <p>Peer-reviewed publications</p>
          <span>02 papers</span>
        </div>
        {publications.map((publication, index) => (
          <motion.a
            className="publication-item"
            href={publication.url}
            target="_blank"
            rel="noreferrer"
            key={publication.title}
            initial={reduceMotion ? false : { opacity: 0, x: -22 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.35 }}
            transition={{ duration: 0.5, delay: reduceMotion ? 0 : index * 0.08 }}
          >
            <span className="publication-item__index">0{index + 1}</span>
            <div>
              <p>{publication.venue} · {publication.year}</p>
              <h3>{publication.title}</h3>
              <span>{publication.authors}</span>
            </div>
            <FiArrowUpRight aria-hidden="true" />
          </motion.a>
        ))}
      </div>
    </>
  );
};

export default AppWrap(
  MotionWrap(Research, 'app__research'),
  'research',
  'app__whitebg',
);
